import {
	type DependencyList, type RefObject, useLayoutEffect, useRef, useState,
} from 'react';

/**
 * Whether the element's own text is clipped (its content is wider than its box), so callers can
 * show the full label as a tooltip only when it is actually cut off.
 *
 * @param {RefObject<HTMLElement|null>} ref  The element whose text may be clipped.
 * @param {DependencyList}              deps Values that change the element's content, triggering a recheck.
 * @return {boolean} True while the text is clipped.
 */
export function useIsTruncated( ref: RefObject< HTMLElement | null >, deps: DependencyList ): boolean {
	const [ isTruncated, setIsTruncated ] = useState( false );

	useLayoutEffect( () => {
		const el = ref.current;
		if ( ! el ) {
			return undefined;
		}

		const recompute = () => setIsTruncated( el.scrollWidth > el.clientWidth );
		recompute();

		// Also recheck when the element's own rendered width changes (e.g. it mounts
		// inside a still-collapsed panel and only gets real width once opened).
		const resizeObserver = new ResizeObserver( recompute );
		resizeObserver.observe( el );
		return () => resizeObserver.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps );

	return isTruncated;
}

function fitsWithinMaxLines( widths: number[], containerWidth: number, gapPx: number, maxLines: number ): boolean {
	let lines = 1;
	let lineWidth = 0;
	let overflowed = false;
	widths.forEach( ( width ) => {
		const nextWidth = lineWidth === 0 ? width : lineWidth + gapPx + width;
		if ( nextWidth > containerWidth && lineWidth !== 0 ) {
			lines += 1;
			lineWidth = width;
			if ( lines > maxLines ) {
				overflowed = true;
			}
		} else {
			lineWidth = nextWidth;
		}
	} );
	return ! overflowed;
}

type FittedChipOptions = {
	// Must match the chip row's own `gap-*` class.
	gapPx: number;
	maxLines: number;
	// Content of the trailing "+N" chip, when the row has one; changing it re-measures.
	overflowPlaceholder?: string;
};

/**
 * Measures the real (rendered) chip widths in a hidden nowrap row, then simulates the
 * container's flex-wrap packing in JS to find how many chips fit within `maxLines`.
 *
 * Chips in the measuring row are marked `data-fit-chip`. If the row also holds a
 * `data-fit-overflow` chip, room is reserved for it whenever some chips are left out.
 *
 * @param {string}            chipsKey                      Changes whenever the chip content does, triggering a
 *                                                          re-measure.
 * @param {FittedChipOptions} options
 * @param {number}            options.gapPx                 The chip row's gap, in pixels.
 * @param {number}            options.maxLines              How many lines the visible chips may wrap onto.
 * @param {string}            [options.overflowPlaceholder] Content of the trailing "+N" chip, when the row has one.
 * @return {Object} Refs for the visible row and the hidden measuring row, and how many chips fit.
 */
export function useFittedChipCount(
	chipsKey: string,
	{ gapPx, maxLines, overflowPlaceholder }: FittedChipOptions,
) {
	const containerRef = useRef<HTMLDivElement>( null );
	const measureRef = useRef<HTMLDivElement>( null );
	const [ visibleCount, setVisibleCount ] = useState( Infinity );

	useLayoutEffect( () => {
		const container = containerRef.current;
		const measure = measureRef.current;
		if ( ! container || ! measure ) {
			return undefined;
		}

		const recompute = () => {
			const containerWidth = container.getBoundingClientRect().width;
			if ( ! containerWidth ) {
				return;
			}

			const chipWidths = Array.from(
				measure.querySelectorAll<HTMLElement>( '[data-fit-chip]' ),
			).map( ( el ) => el.getBoundingClientRect().width );
			const overflowChip = measure.querySelector<HTMLElement>( '[data-fit-overflow]' );
			const fits = ( widths: number[] ) => fitsWithinMaxLines( widths, containerWidth, gapPx, maxLines );

			let count = chipWidths.length;
			while ( count > 0 && ! fits( chipWidths.slice( 0, count ) ) ) {
				count -= 1;
			}
			if ( overflowChip && count < chipWidths.length ) {
				const overflowWidth = overflowChip.getBoundingClientRect().width;
				while ( count > 0 && ! fits( [ ...chipWidths.slice( 0, count ), overflowWidth ] ) ) {
					count -= 1;
				}
			}

			setVisibleCount( count );
		};

		recompute();

		// Coalesce to at most one recompute per frame — a window drag can fire many
		// ResizeObserver callbacks per second, and running the measurement/setState on
		// every single one is what causes the layout to stutter while resizing.
		let animationFrame: number | null = null;
		const scheduleRecompute = () => {
			if ( animationFrame !== null ) {
				return;
			}
			animationFrame = requestAnimationFrame( () => {
				animationFrame = null;
				recompute();
			} );
		};

		const resizeObserver = new ResizeObserver( scheduleRecompute );
		resizeObserver.observe( container );
		measure.querySelectorAll<HTMLElement>(
			'[data-fit-chip], [data-fit-overflow]',
		).forEach( ( element ) => resizeObserver.observe( element ) );
		return () => {
			resizeObserver.disconnect();
			if ( animationFrame !== null ) {
				cancelAnimationFrame( animationFrame );
			}
		};
		// `chipsKey`/`overflowPlaceholder` intentionally re-trigger measurement when the
		// underlying chip content changes.
	}, [ chipsKey, overflowPlaceholder, gapPx, maxLines ] );

	return { containerRef, measureRef, visibleCount };
}
